import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import EmptyState from '../components/EmptyState';

function formatarData(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function EnviarNotificacao() {
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [url, setUrl] = useState('');
  const [tipo, setTipo] = useState('broadcast'); // 'broadcast' | 'cliente'
  const [clienteId, setClienteId] = useState('');
  const [clientes, setClientes] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const carregarHistorico = async () => {
    try {
      const { data } = await api.get('/notifications/historico?limit=20');
      setHistorico(data);
    } catch { /* permissão pode faltar ou erro silencioso */ }
  };

  useEffect(() => {
    carregarHistorico();
    api.get('/clientes').then(({ data }) => setClientes(data)).catch(() => {});
  }, []);

  const enviar = async (e) => {
    e.preventDefault();
    if (!titulo.trim() || !corpo.trim()) {
      return Swal.fire({ icon: 'warning', title: 'Preencha título e mensagem', confirmButtonColor: '#2e9846' });
    }
    if (tipo === 'cliente' && !clienteId) {
      return Swal.fire({ icon: 'warning', title: 'Escolha o cliente destinatário', confirmButtonColor: '#2e9846' });
    }

    const r = await Swal.fire({
      icon: 'question',
      title: 'Confirmar envio?',
      html: tipo === 'broadcast'
        ? '<div>A notificação será enviada para <strong>todos os clientes ativos</strong> com push ativado.</div>'
        : `<div>A notificação será enviada apenas para o cliente selecionado.</div>`,
      showCancelButton: true,
      confirmButtonColor: '#2e9846',
      confirmButtonText: 'Enviar',
    });
    if (!r.isConfirmed) return;

    setEnviando(true);
    try {
      const { data } = await api.post('/notifications/enviar', {
        titulo,
        corpo,
        url: url || null,
        destinatario_tipo: tipo,
        destinatario_id: tipo === 'cliente' ? parseInt(clienteId, 10) : null,
      });
      Swal.fire({
        icon: 'success',
        title: 'Enviado',
        html: `<div>Alvos: <strong>${data.total_alvos}</strong></div>
               <div>Enviadas: <strong>${data.enviadas}</strong></div>
               <div>Falhas: <strong>${data.falhas}</strong></div>`,
        confirmButtonColor: '#2e9846',
      });
      setTitulo(''); setCorpo(''); setUrl(''); setClienteId('');
      carregarHistorico();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <div className="page-header-cli">
        <h1 className="page-title-cli"><i className="bi bi-megaphone-fill"></i> Notificações</h1>
      </div>

      <form className="card-cli" onSubmit={enviar} style={{ maxWidth: 720 }}>
        <h2 style={{ marginTop: 0, fontSize: '1.05rem', color: 'var(--verde-escuro)' }}>Enviar nova notificação</h2>

        <div style={{ marginBottom: 12 }}>
          <label className="label-cli">Destinatário</label>
          <div className="toggle-modo-cli" style={{ margin: 0 }}>
            <button type="button" className={tipo === 'broadcast' ? 'ativo' : ''} onClick={() => setTipo('broadcast')}>
              Todos os clientes
            </button>
            <button type="button" className={tipo === 'cliente' ? 'ativo' : ''} onClick={() => setTipo('cliente')}>
              Cliente específico
            </button>
          </div>
        </div>

        {tipo === 'cliente' && (
          <div style={{ marginBottom: 12 }}>
            <label className="label-cli">Cliente</label>
            <select className="input-cli" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">— selecione —</option>
              {clientes.filter(c => c.ativo && c.vinculado).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome} ({c.cgc}) — {c.qtd_devices} device{c.qtd_devices === 1 ? '' : 's'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label className="label-cli">Título</label>
          <input
            className="input-cli"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            maxLength={120}
            placeholder="Ex.: Boleto disponível"
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label className="label-cli">Mensagem</label>
          <textarea
            className="input-cli"
            value={corpo}
            onChange={(e) => setCorpo(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="Escreva o aviso a ser enviado."
          />
          <small style={{ color: 'var(--texto-suave)' }}>{corpo.length}/500</small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="label-cli">URL ao clicar (opcional)</label>
          <input
            className="input-cli"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/home"
          />
        </div>

        <button type="submit" className="btn-verde-cli" disabled={enviando}>
          <i className="bi bi-send-fill"></i>
          {enviando ? 'Enviando…' : 'Enviar notificação'}
        </button>
      </form>

      <div className="card-cli">
        <h2 style={{ marginTop: 0, fontSize: '1.05rem', color: 'var(--verde-escuro)' }}>Histórico de envios</h2>
        {historico.length === 0 ? (
          <EmptyState icon="bi-clock-history" titulo="Nenhum envio ainda" mensagem="Use o formulário acima para mandar a primeira notificação." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="tabela-cli">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Título</th>
                  <th>Para</th>
                  <th>Alvos</th>
                  <th>Enviadas / Falhas</th>
                  <th>Por</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id}>
                    <td>{formatarData(h.enviado_em)}</td>
                    <td>{h.titulo}</td>
                    <td>
                      {h.destinatario_tipo === 'broadcast'
                        ? <span className="tag-cli">Todos</span>
                        : <span className="tag-cli laranja">{h.destinatario_nome || '#' + h.destinatario_id}</span>}
                    </td>
                    <td>{h.total_alvos}</td>
                    <td>
                      <span style={{ color: 'var(--verde-escuro)' }}>{h.enviadas}</span>
                      {' / '}
                      <span style={{ color: h.falhas ? '#9b2424' : 'var(--texto-suave)' }}>{h.falhas}</span>
                    </td>
                    <td>{h.enviado_por_nome || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
